import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import type { WorkspaceConversationState } from '../components/Workspace/types';

type Settings = {
  slackWebhook?: string;
  theme?: 'light' | 'dark' | 'system';
  workspaceName?: string;
};
type Connected = { providers: string[] };
type UploadBuffer = { droppedFiles: File[] };
const DEFAULT_ASSISTANT_MESSAGE = {
  id: 'welcome-message',
  role: 'assistant' as const,
  content: 'I have access to your workspace knowledge. What would you like to analyze today?',
  timestamp: new Date().toISOString(),
};

const defaultConversationState: WorkspaceConversationState = {
  planId: null,
  plan: null,
  steps: [],
  messages: [DEFAULT_ASSISTANT_MESSAGE],
  suggestedActions: [],
};

const loadConversationState = (): WorkspaceConversationState => {
  try {
    const raw = localStorage.getItem('insora.conversation');
    if (!raw) {
      return defaultConversationState;
    }
    const parsed = JSON.parse(raw) as Partial<WorkspaceConversationState>;
    if (!parsed || typeof parsed !== 'object') {
      return defaultConversationState;
    }
    return {
      planId: parsed.planId ?? null,
      plan: parsed.plan ?? null,
      steps: Array.isArray(parsed.steps) ? parsed.steps : [],
      messages:
        Array.isArray(parsed.messages) && parsed.messages.length > 0
          ? parsed.messages.map((message) => ({
              ...message,
              timestamp: message.timestamp || new Date().toISOString(),
            }))
          : defaultConversationState.messages,
      suggestedActions: Array.isArray(parsed.suggestedActions)
        ? parsed.suggestedActions.map((action: any) => ({
            ...action,
            formValues: action?.formValues ?? {},
            parameterTypes: action?.parameterTypes ?? {},
          }))
        : [],
    };
  } catch {
    return defaultConversationState;
  }
};

export type ActionStreamStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

export interface ActionLogEntry {
  id: string;
  timestamp: string;
  event: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ActionStreamActionState {
  actionId: string;
  actionType?: string;
  planId?: string | null;
  stepId?: string | null;
  status?: string | null;
  approvalStatus?: string | null;
  requiresApproval?: boolean;
  actor?: string | null;
  lastEventAt?: string | null;
  logs: ActionLogEntry[];
}

export interface ActionStreamState {
  planId: string | null;
  status: ActionStreamStatus;
  error: string | null;
  actions: Record<string, ActionStreamActionState>;
}

type ActionStreamAction =
  | { type: 'reset'; planId: string | null }
  | { type: 'connecting' }
  | { type: 'connected' }
  | { type: 'disconnected'; error?: string | null }
  | { type: 'event'; payload: Record<string, unknown> };

const MAX_ACTION_LOG_ENTRIES = 50;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeString = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

const buildLogId = (actionId: string) =>
  `${actionId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const buildDefaultMessage = (
  eventName: string,
  entry: ActionStreamActionState,
  payload: Record<string, unknown>,
): string => {
  switch (eventName) {
    case 'action.staged':
      return entry.actor ? `Action staged by ${entry.actor}.` : 'Action staged.';
    case 'action.approved':
      return entry.actor ? `Action approved by ${entry.actor}.` : 'Action approved.';
    case 'action.rejected':
      return 'Action rejected.';
    case 'action.executed':
      return 'Action executed.';
    case 'action.transition': {
      const transition = normalizeString(payload.transition);
      return transition ? `Transition recorded: ${transition}.` : 'Action transition recorded.';
    }
    case 'plan.step.decision': {
      const decision = normalizeString(payload.decision);
      return decision ? `Step decision: ${decision}.` : 'Step decision recorded.';
    }
    case 'plan.task.progress': {
      const status = normalizeString(payload.status);
      return status ? `Plan status updated: ${status}.` : 'Plan progress updated.';
    }
    default:
      return `Event: ${eventName}`;
  }
};

const buildLogDetails = (payload: Record<string, unknown>): Record<string, unknown> | null => {
  const details: Record<string, unknown> = {};
  const rawDetails = payload.details;
  if (isRecord(rawDetails)) {
    Object.assign(details, rawDetails);
  }
  const supplementalKeys = [
    'result',
    'rationale',
    'reason',
    'task_status',
    'approval_status',
    'requires_approval',
    'transition',
    'status',
  ];
  supplementalKeys.forEach((key) => {
    const value = payload[key];
    if (typeof value !== 'undefined' && value !== null && !(key in details)) {
      details[key] = value;
    }
  });
  return Object.keys(details).length > 0 ? details : null;
};

const initialActionStreamState: ActionStreamState = {
  planId: null,
  status: 'idle',
  error: null,
  actions: {},
};

const reduceStreamEvent = (
  state: ActionStreamState,
  payload: Record<string, unknown>,
): ActionStreamState => {
  if (!isRecord(payload)) {
    return state;
  }
  const eventName = normalizeString(payload.event) ?? normalizeString(payload.event_type) ?? 'message';
  if (eventName === 'heartbeat') {
    if (state.status === 'connecting') {
      return { ...state, status: 'connected', error: null };
    }
    return state;
  }
  const eventPlanId = normalizeString(payload.plan_id);
  if (state.planId && eventPlanId && state.planId !== eventPlanId) {
    return state;
  }
  const nextPlanId = state.planId ?? eventPlanId ?? null;
  const rawActionId = payload.action_id;
  if (typeof rawActionId === 'undefined' || rawActionId === null) {
    return {
      ...state,
      planId: nextPlanId,
      status: state.status === 'connecting' ? 'connected' : state.status,
      error: null,
    };
  }
  const actionId = String(rawActionId);
  const existing = state.actions[actionId] ?? { actionId, logs: [] };
  const timestamp = normalizeString(payload.timestamp) ?? new Date().toISOString();
  const nextEntry: ActionStreamActionState = {
    ...existing,
    actionId,
    logs: existing.logs ?? [],
  };

  if (eventPlanId) {
    nextEntry.planId = eventPlanId;
  }
  const rawStep = payload.step_id;
  if (typeof rawStep !== 'undefined') {
    nextEntry.stepId = rawStep === null ? null : String(rawStep);
  }
  const rawType = normalizeString(payload.action_type);
  if (rawType) {
    nextEntry.actionType = rawType;
  }
  const status = normalizeString(payload.status);
  if (status) {
    nextEntry.status = status;
  }
  if (typeof payload.requires_approval === 'boolean') {
    nextEntry.requiresApproval = payload.requires_approval;
  }
  const approvalStatus = normalizeString(payload.approval_status);
  if (approvalStatus) {
    nextEntry.approvalStatus = approvalStatus;
  }
  const actor = normalizeString(payload.actor);
  if (actor) {
    nextEntry.actor = actor;
  }
  if (eventName === 'plan.step.decision') {
    const decision = normalizeString(payload.decision);
    if (decision) {
      nextEntry.approvalStatus = decision;
      if (decision === 'rejected') {
        nextEntry.status = 'rejected';
      }
    }
    nextEntry.requiresApproval = true;
  }

  const message =
    normalizeString(payload.message) ?? buildDefaultMessage(eventName, nextEntry, payload);
  const detailPayload = buildLogDetails(payload);

  let updatedLogs = [
    ...nextEntry.logs,
    {
      id: buildLogId(actionId),
      timestamp,
      event: eventName,
      message,
      details: detailPayload ?? undefined,
    } satisfies ActionLogEntry,
  ];
  if (updatedLogs.length > MAX_ACTION_LOG_ENTRIES) {
    updatedLogs = updatedLogs.slice(updatedLogs.length - MAX_ACTION_LOG_ENTRIES);
  }
  nextEntry.logs = updatedLogs;
  nextEntry.lastEventAt = timestamp;

  return {
    planId: nextPlanId,
    status: state.status === 'connecting' ? 'connected' : state.status,
    error: null,
    actions: {
      ...state.actions,
      [actionId]: nextEntry,
    },
  };
};

const actionStreamReducer = (
  state: ActionStreamState,
  action: ActionStreamAction,
): ActionStreamState => {
  switch (action.type) {
    case 'reset':
      if (state.planId === action.planId) {
        return {
          ...state,
          status: action.planId ? 'connecting' : 'idle',
          error: null,
        };
      }
      return {
        planId: action.planId,
        status: action.planId ? 'connecting' : 'idle',
        error: null,
        actions: {},
      };
    case 'connecting':
      return { ...state, status: 'connecting', error: null };
    case 'connected':
      return { ...state, status: 'connected', error: null };
    case 'disconnected':
      return { ...state, status: 'disconnected', error: action.error ?? state.error };
    case 'event':
      return reduceStreamEvent(state, action.payload);
    default:
      return state;
  }
};

type AppState = {
  settings: Settings;
  connected: Connected;
  upload: UploadBuffer;
  conversation: WorkspaceConversationState;
  actionStream: ActionStreamState;
  setSettings: (s: Settings) => void;
  setConnected: (c: Connected) => void;
  setUpload: (u: UploadBuffer) => void;
  setConversation: React.Dispatch<React.SetStateAction<WorkspaceConversationState>>;
  dispatchActionStream: (action: ActionStreamAction) => void;
};

const Ctx = createContext<AppState | null>(null);

export const AppStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(() => {
    try {
      const raw = localStorage.getItem('insora.settings');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [connected, setConnected] = useState<Connected>({ providers: [] });
  const [upload, setUpload] = useState<UploadBuffer>({ droppedFiles: [] });
  const [conversation, setConversation] =
    useState<WorkspaceConversationState>(loadConversationState);
  const [actionStream, dispatchActionStreamInternal] = useReducer(
    actionStreamReducer,
    initialActionStreamState,
  );

  const dispatchActionStream = useCallback((action: ActionStreamAction) => {
    dispatchActionStreamInternal(action);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('insora.conversation', JSON.stringify(conversation));
    } catch {
      /* noop */
    }
  }, [conversation]);

  const value = useMemo(
    () => ({
      settings,
      connected,
      upload,
      conversation,
      actionStream,
      setSettings,
      setConnected,
      setUpload,
      setConversation,
      dispatchActionStream,
    }),
    [settings, connected, upload, conversation, actionStream, dispatchActionStream],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// Persist settings and apply theme
export const AppStateEffect: React.FC = () => {
  const { settings } = useAppState();
  React.useEffect(() => {
    try {
      localStorage.setItem('insora.settings', JSON.stringify(settings));
    } catch {
      /* noop */
    }
    const theme = settings.theme || 'system';
    const root = document.documentElement;
    const prefersDark =
      window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const dark = theme === 'dark' || (theme === 'system' && prefersDark);
    root.classList.toggle('dark', !!dark);
  }, [settings]);
  return null;
};

export const useAppState = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAppState must be used within AppStateProvider');
  return ctx;
};
